import { api, ApiException } from './client';
import { useAuth, type AuthUser } from '../store/auth';
import type { components } from './types';
import type { Goal } from '../lib/goals';

export type Product = components['schemas']['Product'];
export type PantryEntry = components['schemas']['PantryEntry'];
export type FamilyMember = components['schemas']['FamilyMember'];
export type FamilyMemberInput = components['schemas']['FamilyMemberInput'];
export type RecipeSummary = components['schemas']['RecipeSummary'];
// Сводка + калорийность порции — ответ /recipes/by-calories (экран «Калории»). kcal для бейджа.
export interface RecipeByCalories extends RecipeSummary {
  kcal: number;
}
// Контракт импорта (Phase 1D) новее сгенерированного types.ts. Источник истины —
// api-contract/openapi.yaml (схемы ParsedRecipe/ParsedIngredient) и
// backend/src/recipes/import/routes.ts. Задаём вручную, как RecommendationsResponse.
export interface ParsedIngredient {
  name: string;
  amount: number | null;
  unit: string | null;
  isOptional: boolean;
}
export interface ParsedRecipe {
  title: string;
  cookTimeMin: number;
  servings: number;
  ingredients: ParsedIngredient[];
  steps: string[];
  kbjuPerServing: { kcal: number; protein: number; fat: number; carbs: number };
  dietaryTags: string[];
  allergenTags: string[];
}
export interface ImportPreviewResponse {
  recipe: ParsedRecipe;
  matchedCount: number;
  extra: ParsedIngredient[];
  warnings: string[];
}
export interface ImportConfirmResponse {
  recipeId: string;
}

// Контракт адаптации (Phase 1F) новее сгенерированного types.ts — задаём вручную,
// источник истины — api-contract/openapi.yaml (схема Swap) и backend adapt/routes.ts.
export interface Swap {
  original: string;
  replacement: string;
  reason: string;
}
export interface AdaptResponse {
  swaps: Swap[];
}

// Контракт биллинга (Phase 1G) — задаём вручную, источник истины
// backend/src/billing/routes.ts + entitlement.ts. types.ts НЕ регенерируем.
export type Tier = 'free' | 'pro' | 'family';
export interface Subscription {
  tier: Tier;
  active: boolean;
  currentPeriodEnd: string | null;
}
export interface CheckoutResponse {
  paymentId: string;
  confirmationUrl: string;
}

// Ответ /auth/telegram — та же форма, что у SMS-verify (источник истины backend/src/auth/routes.ts).
export interface TelegramLoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
  consentRequired: boolean;
}

// Ответ GET /auth/me — профиль + статус согласия (источник истины backend/src/auth/routes.ts).
// Нужен возвращающимся юзерам: фронт не вызывает /auth/refresh, флаг согласия берём отсюда.
export interface MeResponse {
  id: string;
  phone: string | null;
  createdAt: string;
  consentRequired: boolean;
  specialConsent: boolean;
}

// extraIngredients есть в обновлённом контракте 1D (getRecipe), но не в сгенерированном
// types.ts — добавляем вручную (display-only, несопоставленные с каталогом).
export type RecipeFull = components['schemas']['RecipeFull'] & {
  extraIngredients?: ParsedIngredient[];
  isFavorite?: boolean;
};

// /recommendations отсутствует в сгенерированном types.ts (контракт Phase 1B новее).
// Форма ответа — из backend/src/recommendations/routes.ts (источник истины).
export interface RecommendationItem {
  recipeId: string;
  reason: string;
  recipe: RecipeSummary | null;
}
export interface RecommendationsResponse {
  items: RecommendationItem[];
  fromCache: boolean;
}

// Планировщик меню (Чанк 3, контракт meal-plan/routes.ts) — задаём вручную, types.ts НЕ регенерируем.
export type MealSlot = 'breakfast' | 'lunch_soup' | 'lunch_main' | 'lunch_salad' | 'dinner_main' | 'dinner_salad';
export interface MealPlanEntry {
  dayIndex: number;
  slot: MealSlot;
  recipe: RecipeSummary;
}
export interface MealPlan {
  weekStart: string;
  entries: MealPlanEntry[];
}
export interface GeneratedMealPlan extends MealPlan {
  lowVariety: boolean;
}
export interface ShoppingItem {
  productId: string;
  name: string;
  unit: string;
  amount: number | null;
  inPantry: boolean;
  category: 'dairy' | 'meat' | 'vegetables' | 'grains' | 'other';
}
export interface ShoppingList {
  items: ShoppingItem[];
  extras: Array<{ name: string }>;
}

// Строгий подбор «приготовить из холодильника» (контракт recommendations/routes.ts /cook).
// missingCount===0 — можно готовить сейчас; 1..2 — почти готово (докупить 1-2).
export interface CookItem {
  recipe: RecipeSummary;
  missingCount: number;
}

function token(): string | null {
  return useAuth.getState().accessToken;
}

// На 401 разлогиниваем — Protected уведёт на /login. Остальные ошибки прокидываем.
// Перед чисткой стора отзываем refresh-токен на сервере (fire-and-forget): сессия на
// устройстве закончилась — токен в БД не должен жить ещё 30 дней. Ошибку глотаем:
// выход не должен ломаться из-за недоступности сервера.
async function authed<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (e) {
    if (e instanceof ApiException && e.status === 401) {
      const { refreshToken } = useAuth.getState();
      if (refreshToken) void endpoints.logout(refreshToken).catch(() => undefined);
      useAuth.getState().logout();
    }
    throw e;
  }
}

// Единый разбор гейт-ответа 402. Сообщение берём с сервера; requiredTier — pro|family
// (любой иной/отсутствующий → pro как безопасный дефолт). Используется во всех gated-экранах.
export function paymentRequired(e: unknown): { requiredTier: 'pro' | 'family'; message: string } | null {
  if (e instanceof ApiException && e.status === 402) {
    return {
      requiredTier: e.body.details?.requiredTier === 'family' ? 'family' : 'pro',
      message: e.body.message,
    };
  }
  return null;
}

export const endpoints = {
  loginWithTelegram: (initData: string) =>
    api.post<TelegramLoginResponse>('/auth/telegram', { initData }),
  // Профиль текущего юзера + статус согласия. Возвращающийся юзер так узнаёт consentRequired
  // (фронт не вызывает /auth/refresh). authed: на 401 разлогинит.
  getMe: () => authed(() => api.get<MeResponse>('/auth/me', token())),
  // Зафиксировать согласие на обработку ПДн за текущим юзером (идемпотентно, сервер отвечает 204).
  // special=true дополнительно фиксирует согласие на спец-категории (Часть 2: здоровье/диета).
  acceptConsent: (special: boolean) => authed(() => api.post<void>('/auth/consent', { special }, token())),
  // Выход: отзыв refresh-токена на сервере. Без authed и без Bearer — работает и с
  // протухшим access (бэкенд принимает только refreshToken в теле, отвечает 204).
  logout: (refreshToken: string) => api.post<void>('/auth/logout', { refreshToken }),
  // Продуктовая аналитика (тихий счётчик). НАМЕРЕННО без authed: фоновый флаш с протухшим
  // токеном не должен разлогинивать пользователя. token() может быть null — сервер примет
  // событие как анонимное. Ошибки глотает вызывающий (lib/analytics).
  // keepalive: true — браузер дошлёт запрос даже если webview закрывают (swipe в Telegram/iOS PWA),
  // иначе события, накопленные на старте (app_open, первый screen_view), теряются при закрытии.
  sendEvents: (events: Array<{ event: string; props?: Record<string, unknown> }>) =>
    api.post<void>('/events', { events }, token(), { keepalive: true }),
  getProducts: (params: { search?: string; limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return authed(() => api.get<Product[]>(`/products${suffix}`, token()));
  },
  getPantry: () => authed(() => api.get<PantryEntry[]>('/pantry', token())),
  addPantryProducts: (productIds: string[]) =>
    authed(() => api.post<{ ok: boolean }>('/pantry/products', { productIds }, token())),
  removePantryProduct: (productId: string) =>
    authed(() => api.delete<void>(`/pantry/products/${productId}`, token())),
  getFamily: () => authed(() => api.get<FamilyMember[]>('/family/members', token())),
  addFamilyMember: (input: FamilyMemberInput) =>
    authed(() => api.post<FamilyMember>('/family/members', input, token())),
  updateFamilyMember: (id: string, input: FamilyMemberInput) =>
    authed(() => api.put<FamilyMember>(`/family/members/${id}`, input, token())),
  deleteFamilyMember: (id: string) =>
    authed(() => api.delete<void>(`/family/members/${id}`, token())),
  getRecommendations: () =>
    authed(() => api.post<RecommendationsResponse>('/recommendations', undefined, token())),
  cookFromPantry: (courses?: string[]) =>
    authed(() =>
      api.post<{ items: CookItem[] }>(
        '/recommendations/cook',
        courses && courses.length ? { courses } : {},
        token(),
      ),
    ),
  getRecipe: (id: string) => authed(() => api.get<RecipeFull>(`/recipes/${id}`, token())),
  getMyRecipes: () => authed(() => api.get<RecipeSummary[]>('/recipes/mine', token())),
  getFavorites: () => authed(() => api.get<RecipeSummary[]>('/recipes/favorites', token())),
  addFavorite: (id: string) => authed(() => api.put<void>(`/recipes/${id}/favorite`, undefined, token())),
  removeFavorite: (id: string) => authed(() => api.delete<void>(`/recipes/${id}/favorite`, token())),
  importPreview: (url: string) =>
    authed(() => api.post<ImportPreviewResponse>('/recipes/import/preview', { url }, token())),
  importConfirm: (payload: { url: string; recipe: ParsedRecipe; isShared: boolean }) =>
    authed(() => api.post<ImportConfirmResponse>('/recipes/import/confirm', payload, token())),
  adaptRecipe: (recipeId: string, familyMemberId: string) =>
    authed(() => api.post<AdaptResponse>(`/recipes/${recipeId}/adapt`, { familyMemberId }, token())),
  getSubscription: () => authed(() => api.get<Subscription>('/billing/subscription', token())),
  checkout: (tier: 'pro' | 'family') =>
    authed(() => api.post<CheckoutResponse>('/billing/checkout', { tier }, token())),
  claim: (paymentId: string) =>
    authed(() => api.post<Subscription>('/billing/claim', { paymentId }, token())),
  claimPending: () =>
    authed(() => api.post<Subscription>('/billing/claim-pending', undefined, token())),
  getMealPlan: () => authed(() => api.get<MealPlan>('/meal-plan', token())),
  setMealEntry: (dayIndex: number, slot: MealSlot, recipeId: string | null) =>
    authed(() => api.put<{ ok: boolean }>('/meal-plan/entry', { dayIndex, slot, recipeId }, token())),
  rerollMealEntry: (dayIndex: number, slot: MealSlot) =>
    authed(() => api.post<{ recipe: RecipeSummary | null }>('/meal-plan/entry/reroll', { dayIndex, slot }, token())),
  generateMealPlan: (goal?: Goal, slots?: MealSlot[]) => {
    const body: { goal?: Goal; slots?: MealSlot[] } = {};
    if (goal) body.goal = goal;
    if (slots) body.slots = slots;
    const has = Object.keys(body).length > 0;
    return authed(() => api.post<GeneratedMealPlan>('/meal-plan/generate', has ? body : undefined, token()));
  },
  getShoppingList: (servings?: number) =>
    authed(() => api.get<ShoppingList>(`/meal-plan/shopping-list${servings ? `?servings=${servings}` : ''}`, token())),
  // course='' → «показать все»: бэкенд-zod принимает только 9 значений enum, пустая строка дала бы 400,
  // поэтому при пустом значении НЕ шлём параметр course.
  getRecipesByCourse: (course: string) => {
    const qs = course ? `?course=${encodeURIComponent(course)}&limit=100` : `?limit=100`;
    return authed(() =>
      api.get<{ items: RecipeSummary[]; total: number; page: number; limit: number }>(`/recipes${qs}`, token()),
    );
  },
  getCuisines: () =>
    authed(() =>
      api.get<Array<{ cuisine: string; count: number }>>('/recipes/cuisines', token()),
    ),
  // cuisine всегда одно из 9 значений (в отличие от course='' = «все»): шлём параметр всегда.
  getRecipesByCuisine: (cuisine: string, limit = 100) =>
    authed(() =>
      api.get<{ items: RecipeSummary[]; total: number; page: number; limit: number }>(
        `/recipes?cuisine=${encodeURIComponent(cuisine)}&limit=${limit}`,
        token(),
      ),
    ),
  getRecipesByProduct: (productId: string, limit = 50) =>
    authed(() =>
      api.get<{ items: RecipeSummary[] }>(
        `/recipes/by-product?productId=${encodeURIComponent(productId)}&limit=${limit}`,
        token(),
      ),
    ),
  // Праздничные блюда (экран «Праздничное»). occasion — home (полный стол) | work (что принести коллегам).
  getFestiveRecipes: (occasion: 'home' | 'work', limit = 50) =>
    authed(() =>
      api.get<{ items: RecipeSummary[] }>(
        `/recipes/festive?occasion=${occasion}&limit=${limit}`,
        token(),
      ),
    ),
  // Рецепты не более maxKcal на порцию (экран «Калории»), от самых лёгких. kcal — на бейдж карточки.
  getRecipesByCalories: (maxKcal: number, limit = 50) =>
    authed(() =>
      api.get<{ items: RecipeByCalories[] }>(
        `/recipes/by-calories?maxKcal=${maxKcal}&limit=${limit}`,
        token(),
      ),
    ),
  // Рецепты не дольше maxMinutes (экран «Быстрые»), от самых быстрых. Время уже на карточке.
  getRecipesByTime: (maxMinutes: number, limit = 50) =>
    authed(() =>
      api.get<{ items: RecipeSummary[] }>(
        `/recipes/by-time?maxMinutes=${maxMinutes}&limit=${limit}`,
        token(),
      ),
    ),
};
